/**
 * Which engagement agreement a client is on (Lloyd, Aug 21 2026 — "both paths": existing clients
 * sign the addendum, new clients sign the new agreement).
 *
 * A version alone does not put the newer terms — the failure-to-appear fee included — into force:
 * a signature date is also required. The backend's `describeAgreement` says which terms apply and
 * whether a signature is still awaited, and that reading is what the panel shows.
 */

export type AgreementVersion = 'LEGACY' | 'ADDENDUM' | 'NEW_AGREEMENT';

export const AGREEMENT_LABELS: Record<AgreementVersion, string> = {
  LEGACY: 'Original agreement — new terms not signed',
  ADDENDUM: 'Addendum signed',
  NEW_AGREEMENT: 'New engagement agreement signed',
};

export interface AgreementStatus {
  version: AgreementVersion;
  newTermsApply: boolean;
  awaitingSignature: boolean;
  reason: string;
}

export interface ClientAgreementDto {
  User_guid: string;
  User_AgreementVersion: AgreementVersion;
  User_AgreementSignedAt: string | null;
  User_AgreementRecordedBy: string | null;
  status: AgreementStatus;
}

export interface PendingAgreementRow {
  User_guid: string;
  name: string;
  version: AgreementVersion;
  signedAt: string | null;
  status: AgreementStatus;
}

export interface PendingAgreementsDto {
  total: number;
  onNewTerms: number;
  awaitingSignature: PendingAgreementRow[];
  needAddendum: PendingAgreementRow[];
}

export interface RecordAgreementInput {
  User_AgreementVersion: AgreementVersion;
  User_AgreementSignedAt?: string | null;
}

export default class AgreementApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async listPending(): Promise<PendingAgreementsDto> {
    const response = await fetch(
      `${AgreementApi.host()}/api/v1/manager/agreements/pending`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to load clients who still need the addendum');
    return response.json();
  }

  static async get(userGuid: string): Promise<ClientAgreementDto> {
    const response = await fetch(
      `${AgreementApi.host()}/api/v1/manager/user/${userGuid}/agreement`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to load which agreement this client is on');
    return response.json();
  }

  static async record(
    userGuid: string,
    input: RecordAgreementInput,
  ): Promise<ClientAgreementDto> {
    const response = await fetch(
      `${AgreementApi.host()}/api/v1/manager/user/${userGuid}/agreement`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to record the agreement');
    }
    return response.json();
  }
}
